/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, BoxView } from '@components/atoms'
import { mono, boShared } from '@components/systems/backoffice/backoffice.tokens'

import { useAppSelector, useAppDispatch, logDashboardActions } from '@redux/index'
import type { LogRecipientType } from '@redux/types'

import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Switch from '@mui/material/Switch'
import CircularProgress from '@mui/material/CircularProgress'

/**
 * 이메일 수신자 설정 — 발신자·스케줄 스펙 행 → 추가 행 → 수신자 헤어라인 목록. 블랙 CTA 는 헤더의 저장 하나.
 */
const LogSettingsPage = () => {
	const dispatch = useAppDispatch()

	const logSetting = useAppSelector(state => state.logDashboard.logSetting)
	const logSettingStatus = useAppSelector(state => state.logDashboard.logSettingStatus)

	const [newEmail, setNewEmail] = React.useState('')
	const [newName, setNewName] = React.useState('')

	React.useEffect(() => {
		dispatch(logDashboardActions.TRIGGER_GET_LOG_SETTING(true))
	}, [])

	const handleToggleActive = (idx: number) => {
		if (!logSetting) return
		const newRecipients = logSetting.recipients.map((r: LogRecipientType, i: number) =>
			i === idx ? { ...r, active: !r.active } : r,
		)
		dispatch(
			logDashboardActions.LOG_SETTING({ ...logSetting, recipients: newRecipients }),
		)
	}

	const handleDelete = (idx: number) => {
		if (!logSetting) return
		const newRecipients = logSetting.recipients.filter(
			(_: LogRecipientType, i: number) => i !== idx,
		)
		dispatch(
			logDashboardActions.LOG_SETTING({ ...logSetting, recipients: newRecipients }),
		)
	}

	const handleAdd = () => {
		if (!newEmail || !newName || !logSetting) return
		const newRecipient: LogRecipientType = {
			email: newEmail,
			name: newName,
			active: true,
		}
		dispatch(
			logDashboardActions.LOG_SETTING({
				...logSetting,
				recipients: [...logSetting.recipients, newRecipient],
			}),
		)
		setNewEmail('')
		setNewName('')
	}

	const handleSave = () => {
		dispatch(logDashboardActions.TRIGGER_UPDATE_LOG_SETTING(true))
	}

	const isLoading = logSettingStatus === 'loading'

	return (
		<View css={style}>
			<BoxView
				title="이메일 수신자 설정"
				rightHeaderAccessory={
					<Button variant="contained" onClick={handleSave} disabled={isLoading}>
						저장
					</Button>
				}
			>
				<View className="settingsContainer">
					{isLoading ? (
						<View className="loadingView">
							<CircularProgress size={24} />
						</View>
					) : (
						<>
							{logSetting && (
								<View className="spec">
									<View className="boSpecRow">
										<span className="boSpecLabel">발신자</span>
										<span className="boSpecValue">{logSetting.sender}</span>
									</View>
									<View className="boSpecRow">
										<span className="boSpecLabel">스케줄</span>
										<span className="boSpecValue">{logSetting.schedule}</span>
									</View>
								</View>
							)}

							<View className="addRow">
								<TextField
									label="이름"
									value={newName}
									onChange={e => setNewName(e.target.value)}
									size="small"
								/>
								<TextField
									label="이메일"
									value={newEmail}
									onChange={e => setNewEmail(e.target.value)}
									size="small"
									type="email"
									style={{ flex: 1 }}
								/>
								<Button
									variant="outlined"
									onClick={handleAdd}
									disabled={!newEmail || !newName}
								>
									추가
								</Button>
							</View>

							<View className="list">
								<View className="listHead">
									<span>#</span>
									<span>이름</span>
									<span>이메일</span>
									<span className="center">활성</span>
									<span className="center">삭제</span>
								</View>
								{(logSetting?.recipients ?? []).map(
									(recipient: LogRecipientType, idx: number) => (
										<View className="listRow" key={`${recipient.email}-${idx}`}>
											<span className="no">{idx + 1}</span>
											<span>{recipient.name}</span>
											<span className="dim">{recipient.email}</span>
											<span className="center">
												<Switch
													size="small"
													checked={recipient.active}
													onChange={() => handleToggleActive(idx)}
												/>
											</span>
											<span className="center">
												<button
													type="button"
													className="boBtnText"
													onClick={() => handleDelete(idx)}
												>
													삭제
												</button>
											</span>
										</View>
									),
								)}
								{(logSetting?.recipients ?? []).length === 0 && (
									<p className="boEmpty">수신자가 없습니다</p>
								)}
							</View>
						</>
					)}
				</View>
			</BoxView>
		</View>
	)
}

export default LogSettingsPage

const style = css({
	...boShared,
	height: '100%',

	'.settingsContainer': {
		width: '100%',
		maxWidth: 880,
		display: 'flex',
		flexDirection: 'column',
		gap: 28,
	},

	'.loadingView': {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		minHeight: '10rem',
	},

	'.spec': { borderTop: `1px solid ${mono.line}` },

	'.addRow': {
		display: 'flex',
		alignItems: 'center',
		gap: 12,
	},

	'.list': {
		borderTop: `1px solid ${mono.line}`,

		'.listHead, .listRow': {
			display: 'grid',
			gridTemplateColumns: '32px minmax(0, 1fr) minmax(0, 1.6fr) 80px 80px',
			alignItems: 'center',
			gap: 12,
			padding: '8px 0',
			borderBottom: `1px solid ${mono.line}`,
			fontSize: 13,
			fontWeight: 300,
			color: mono.ink,
		},

		'.listHead': {
			padding: '10px 0',
			fontSize: 11,
			fontWeight: 500,
			letterSpacing: '.12em',
			textTransform: 'uppercase',
			color: mono.faint,
		},

		'.listRow:hover': { background: mono.surface },

		'.no': {
			fontSize: 11,
			letterSpacing: '.14em',
			color: mono.faint,
			fontVariantNumeric: 'tabular-nums',
		},
		'.dim': { color: mono.dim },
		'.center': { display: 'flex', justifyContent: 'center' },
	},
})
